<template>
  <div>
    <v-row>
      <v-col cols="12">
        <p>{{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signatureTypeCaption')}}</p>
        <div>
          <AppDownloadFile :link="learnMoreSignatureTypesLink" forceReader class="app-link learnMoreLink">
            <template #default="{ submitForm }">
              <span @click="submitForm">
                {{ $t("project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.learnMoreSignatureType") }}
                <font-awesome-icon :icon="['fal', 'external-link']" size="xs"/>
              </span>
            </template>
          </AppDownloadFile>
        </div>
      </v-col>

      <v-col cols="12">
        <v-radio-group
          :model-value="signatureType"
          required
          :disabled="isEditingLimited"
          class="envelopeType__container"
          inline
          :error-messages="signatureTypeErrorMessages"
        >
          <template v-for="item in envelopeTypeOptions" :key="item.value">
            <AppTooltip :disabled="!item.disabled">
              <template #activator="{ props }">
                <div v-bind="props">
                  <v-radio
                    :true-value="item.value"
                    :disabled="item.disabled"
                    class="envelopeType__card"
                    :class="{'envelopeType__card--active': item.value === signatureType}"
                    readonly
                    @click="!item.disabled && signatureTypeChange(item.value)"
                  >
                    <template #label>
                      <h4>{{ item.text }}</h4>
                      <div class="text-caption">
                        <span v-if="SIGNATURE_TYPE_WITH_AUTH.includes(item.value)" class="mr-1">
                          {{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeAuthRequired') }}
                        </span>
                        <span v-if="item.value === SIGNATURE_TYPE_HANDWRITTEN">
                          {{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeNoOrder') }}
                        </span>
                        <i18n-t
                          keypath="project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeMaxSize"
                          tag="span"
                          scope="global"
                        >
                          <app-filesize-display :value="getProviderMaxSizeInBytes(item.providerMaxSize)"/>
                        </i18n-t>
                      </div>
                    </template>
                  </v-radio>
                </div>
              </template>
              <span>{{ $t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.envelopeTypeOptionDisabledTooltip') }}</span>
            </AppTooltip>
          </template>
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row v-if="signatureType === SIGNATURE_TYPE_HANDWRITTEN">
      <v-col>
        <v-checkbox
          :model-value="hasMercuryOptionActivated"
          class="mt-0"
          density="compact"
          hide-details
          :label="$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.mercuryCaseLabel')"
          @update:model-value="onMercuryOptionChange"
        ></v-checkbox>
      </v-col>
    </v-row>

    <SignerOrderSignatureTypeIncompatibilityWarningDialog
      v-if="signerOrderSignatureTypeIncompatibilityWarningDialog"
      @close="signerOrderSignatureTypeIncompatibilityWarningDialog = false"
      @confirm="confirmChangeSignatureType"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { mapMutations, mapState } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import { BtoMB } from '@/common/utils/sizes'
import {
  SIGNATURE_TYPE_DISPLAY_ORDER,
  SIGNATURE_TYPE_HANDWRITTEN,
  SIGNATURE_TYPE_WITH_AUTH,
} from '@/project/signing-checklist/constants'
import SignerOrderSignatureTypeIncompatibilityWarningDialog from '@/project/signing-checklist/dialogs/SignerOrderSignatureTypeIncompatibilityWarningDialog.vue'
import { SET_IMPORTED_FILE_MERCURY_OPTION } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  disabled: boolean
  providerMaxSize: number
  text: string
  value: string
}

export default defineComponent({
  name: 'SigningChecklistSectionDocumentSignatureType',
  components: { AppTooltip, AppDownloadFile, SignerOrderSignatureTypeIncompatibilityWarningDialog },
  props: {
    signatureType: {
      type: String,
      required: true,
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    isHandwrittenSignatureDisabled: {
      type: Boolean,
      required: false,
    },
    signatureTypeValidator: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    fileSize: {
      type: Number,
      required: true,
    },
    hasSignersOrder: {
      type: Boolean,
      required: true,
    },
    hasMercuryOptionActivated: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      SIGNATURE_TYPE_WITH_AUTH,
      SIGNATURE_TYPE_HANDWRITTEN,
      signerOrderSignatureTypeIncompatibilityWarningDialog: false,
    }
  },
  computed: {
    ...mapState('signingChecklist', ['qualifiedDSWarningIsMuted']),
    ...mapState('room', ['currentRoom', 'roomProvidersPending']),
    learnMoreSignatureTypesLink (): string {
      const locale = this.$i18n.locale as string
      return `${import.meta.env.VUE_APP_API_URL}/assets/pdf?file=signatures-types-${locale.toUpperCase()}`
    },
    signatureTypeErrorMessages () {
      return formFieldErrors(
        this.signatureTypeValidator,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signatureTypeLabel') })],
        ],
      )
    },
    envelopeTypeOptions () {
      const envelopesTypesOptions: Record<string, EnvelopeTypeOption> = {}
      if (!this.roomProvidersPending && this.currentRoom.providers) {
        for (const provider of this.currentRoom.providers) {
          for (const type of provider.types) {
            const value = provider.provider + '.' + type

            if (this.isHandwrittenSignatureDisabled && value === SIGNATURE_TYPE_HANDWRITTEN) continue

            envelopesTypesOptions[value] = {
              text: this.$t(`project.signing-checklist.add.SigningChecklistSectionDocumentSignatureType.signingTypeSelect.${value}`),
              value,
              disabled: this.fileSize > provider.maxSize,
              providerMaxSize: BtoMB(provider.maxSize),
            }
          }
        }
      }

      return SIGNATURE_TYPE_DISPLAY_ORDER
        .map(signatureType => envelopesTypesOptions[signatureType])
        .filter(e => e !== undefined)
    },
  },
  methods: {
    ...mapMutations('signingChecklist', [SET_IMPORTED_FILE_MERCURY_OPTION]),
    signatureTypeChange (signatureType: string | null) {
      const shouldWarnAboutManualSignature = signatureType === SIGNATURE_TYPE_HANDWRITTEN && this.hasSignersOrder
      if (shouldWarnAboutManualSignature) {
        this.signerOrderSignatureTypeIncompatibilityWarningDialog = true
        return
      }
      const shouldWarnAboutQualifiedSignature = signatureType === 'ds.qualified' && !this.qualifiedDSWarningIsMuted

      if (shouldWarnAboutQualifiedSignature) {
        this.$emit('setQualifiedDSWarningDialog', true)
        this.$emit('signature-type-change', this.signatureType)
      } else {
        this.$emit('signature-type-change', signatureType)
      }
    },
    confirmChangeSignatureType () {
      this.signerOrderSignatureTypeIncompatibilityWarningDialog = false
      this.$emit('signature-type-change', SIGNATURE_TYPE_HANDWRITTEN)
    },
    getProviderMaxSizeInBytes (providerMaxSize: number): number {
      return providerMaxSize * 1048576
    },
    onMercuryOptionChange (value: boolean | null) {
      this.$emit('mercury-option-change', value)
    },
  },
})
</script>

<style scoped lang="scss">
.envelopeType__container {
  :deep(.v-selection-control-group) {
    width: 100%;
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  }

  .envelopeType__card {
    width: 100%;
    justify-self: stretch;
    min-height: 72px;
    padding: 16px 24px;
    border: 1px solid #DEE2E6;
    border-radius: 4px;
    margin: 0;
    cursor: pointer;

    &--active {
      outline: 2px solid rgb(var(--v-theme-primary));
      outline-offset: -2px;
      background-color: rgb(var(--v-theme-primary-lighten-4));
    }

    :deep(.v-label){
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }

    .caption {
      color: #717B85;
      font-size: 12px;
    }
  }
}

.learnMoreLink {
  color: #4C75B5;
}
</style>
